/**
 * Rich-text helpers for the Proposal Narrative (and future rich-text
 * surfaces).
 *
 * Two responsibilities:
 *
 *   1. Detect whether a persisted narrative string is HTML or plain
 *      text. This is what unlocks backward compatibility — legacy
 *      narratives authored in the old <Textarea> stay plain and
 *      render via `whitespace-pre-wrap`; new narratives are HTML.
 *
 *   2. Sanitize HTML on the READ path before we inject it into the
 *      DOM. The backend already sanitizes on write via
 *      `html_sanitizer.py`, but we do a second pass on read as
 *      defense-in-depth so any historical row that bypassed
 *      sanitization cannot script the browser.
 */
import DOMPurify from "dompurify";

const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        "p", "div", "br",
        "h1", "h2", "h3", "h4", "h5", "h6",
        "strong", "b", "em", "i", "u", "s", "strike", "code",
        "ul", "ol", "li",
        "blockquote",
        "a",
        "hr",
        "pre",
    ],
    ALLOWED_ATTR: ["href", "title", "rel", "target", "class"],
    ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
    // Never let unknown protocol slip through
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
};

/**
 * Detect if `value` should be rendered as HTML (vs plain text).
 * The heuristic is intentionally simple — a payload is treated as
 * HTML iff its first non-whitespace character is `<`. This matches
 * exactly what TipTap emits (`<p>...</p>`) and what the backend
 * `html_sanitizer` treats as HTML.
 */
export function isHtmlContent(value) {
    if (!value || typeof value !== "string") return false;
    const trimmed = value.trim();
    return trimmed.startsWith("<");
}

/**
 * Wrap a plain-text payload in a single `<p>` per paragraph so it
 * can be handed to TipTap without losing the line-break structure.
 * Consecutive newlines become paragraph boundaries; single newlines
 * become `<br>` inside a paragraph.
 */
export function plainTextToHtml(value) {
    if (!value || typeof value !== "string") return "";
    // Escape any stray < > & to entities before we synthesize the
    // wrapping tags — otherwise plain text containing "<" or "&"
    // would render as broken HTML in the editor.
    const escaped = value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const paragraphs = escaped
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .map((chunk) => `<p>${chunk.replace(/\n/g, "<br>")}</p>`);
    return paragraphs.join("");
}

/**
 * Return the payload in a shape ready to hand to TipTap's `content`
 * prop or to `editor.commands.setContent`. Plain text is upgraded
 * to HTML, HTML passes through unchanged.
 */
export function toEditorContent(value) {
    if (!value) return "";
    return isHtmlContent(value) ? value : plainTextToHtml(value);
}

/**
 * Sanitize an HTML payload before injecting via
 * `dangerouslySetInnerHTML`. Non-HTML input is returned as `null`
 * (the caller then renders the plain-text branch with pre-wrap).
 */
export function sanitizeNarrativeHtml(value) {
    if (!isHtmlContent(value)) return null;
    return DOMPurify.sanitize(value, SANITIZE_CONFIG);
}

/**
 * Convenience — returns `true` when a narrative is semantically
 * empty. TipTap emits `<p></p>` for a cleared editor, which
 * `!value` would miss.
 */
export function isEmptyNarrative(value) {
    if (!value || typeof value !== "string") return true;
    const stripped = value.replace(/<[^>]*>/g, "").trim();
    return stripped.length === 0;
}
